import { useCallback, useMemo } from "react";
import type { Animal } from "../data/animals";
import { shapes } from "../data/shapes";
import { AnimalCard } from "./AnimalCard";
import { useVoiceControl, VoiceButton } from "../voice/VoiceProvider";
import { scrollPage } from "../lib/scroll";
import { heardAny } from "../voice/match";
import { useLocale, useT } from "../i18n/LocaleProvider";
import { aliasesFor, localizeAnimal } from "../i18n/localize";

interface Props {
  onPick: (shape: Animal) => void;
  onHome: () => void;
  completed: Set<string>;
}

export function ShapesPage({ onPick, onHome, completed }: Props) {
  const t = useT();
  const { locale } = useLocale();
  // `shapes` is module data and never went through the localizer — localize for
  // DISPLAY, keep the original for onPick (App localizes on render).
  const items = useMemo(
    () => shapes.map((s) => ({ s, view: localizeAnimal(s, locale) })),
    [locale],
  );
  const onCommand = useCallback(
    (transcript: string): boolean => {
      // Navigation wins over scrolling (see FlagsPage for the reasoning).
      if (heardAny(transcript, [...t.commands.home, ...t.commands.back])) {
        onHome();
        return true;
      }
      if (heardAny(transcript, t.commands.up)) {
        scrollPage(-1);
        return true;
      }
      if (heardAny(transcript, t.commands.down)) {
        scrollPage(1);
        return true;
      }
      for (const { s, view } of items) {
        if (heardAny(transcript, aliasesFor(view, locale))) {
          onPick(s);
          return true;
        }
      }
      return false;
    },
    [onPick, onHome, t],
  );
  useVoiceControl(onCommand);

  return (
    <div className="home">
      <header className="player-header">
        <button className="home-btn" onClick={onHome} aria-label={t.backToHome}>
          {t.home}
        </button>
        <h1>{t.shapesTitle}</h1>
      </header>
      <p className="subtitle">{t.shapesSubtitle}</p>

      <div className="control-bar">
        <VoiceButton />
      </div>

      <div className="card-grid">
        {items.map(({ s, view }) => (
          <AnimalCard
            key={s.id}
            animal={view}
            done={completed.has(s.id)}
            onClick={() => onPick(s)}
          />
        ))}
      </div>
    </div>
  );
}
